import { Exhibitor, ExhibitorCategory } from '@/models/exhibitor';
import { ExhibitorService } from '@/services/exhibitors';
import { errorCatch } from '../utils';

const exhibitorService = new ExhibitorService();

export const state = {
  exhibitors: [],
  exhibitorsOptions: {},
  exhibitorCategoryOptions: {},
  categories: [],
  loading: false,
  error: {}
};

export const mutations = {
  // Sponsor mutations
  ADD_EXHIBITOR(state: any, payload: Exhibitor) {
    state.exhibitors.push(payload);
  },

  SET_EXHIBITORS(state: any, payload: Exhibitor[]) {
    state.exhibitors = payload;
  },

  UPDATE_EXHIBITOR(state: any, payload: Exhibitor) {
    const index = state.exhibitors.findIndex(
      (exhibitor: Exhibitor) => exhibitor.id === payload.id
    );
    state.exhibitors.splice(index, 1, payload);
  },

  REMOVE_EXHIBITOR(state: any, payload: number) {
    const index = state.exhibitors.findIndex(
      (exhibitor: Exhibitor) => exhibitor.id === payload
    );
    state.exhibitors.splice(index, 1);
  },

  // Sponsor Category mutations
  ADD_CATEGORY(state: any, payload: ExhibitorCategory) {
    state.categories.push(payload);
  },

  SET_CATEGORIES(state: any, payload: ExhibitorCategory[]) {
    state.categories = payload;
  },

  UPDATE_CATEGORY(state: any, payload: ExhibitorCategory) {
    const index = state.categories.findIndex(
      (category: ExhibitorCategory) => category.id === payload.id
    );
    state.categories.splice(index, 1, payload);
  },

  REMOVE_CATEGORY(state: any, payload: number) {
    const index = state.categories.findIndex(
      (category: ExhibitorCategory) => category.id === payload
    );
    state.categories.splice(index, 1);
  },

  SET_OPTIONS_CATEGORY(state: any, payload: any) {
    state.exhibitorCategoryOptions = payload;
  },

  SET_OPTIONS(state: any, payload: any) {
    state.exhibitorsOptions = payload;
  },

  SET_LOADING(state: any, payload: boolean) {
    state.loading = payload;
  },

  SET_ERROR(state: any, error: any) {
    errorCatch(state, error);
  },

  CLEAR_ERROR(state: any) {
    state.error = {};
  }
};

export const actions = {
  clearError: ({ commit }: any) => commit('CLEAR_ERROR'),

  // Exhibitor actions
  createExhibitor: async ({ commit, dispatch }: any, payload: Exhibitor) => {
    dispatch('clearError');
    commit('SET_LOADING', true);

    await new Promise((resolve, reject) => {
      exhibitorService
        .createExhibitor(payload)
        .then(exhibitor => {
          commit('ADD_EXHIBITOR', exhibitor);
          resolve();
        })
        .catch(err => {
          commit('SET_ERROR', err);
          //TODO: LF - 2020-03-08: Finalize how to handle error
          reject();
        })
        .finally(() => {
          commit('SET_LOADING', false);
        });
    });
  },

  fetchExhibitors: async (
    { commit, dispatch }: any,
    payload: { event: string; }
  ) => {
    dispatch('clearError');
    commit('SET_LOADING', true);

    await new Promise((resolve, reject) => {
      exhibitorService
        .fetchExhibitors(payload.event)
        .then(exhibitors => {
          commit('SET_EXHIBITORS', exhibitors);
          resolve();
        })
        .catch(err => {
          commit('SET_ERROR', err);
          //TODO: LF - 2020-03-08: Finalize how to handle error
          reject();
        })
        .finally(() => {
          commit('SET_LOADING', false);
        });
    });
  },

  fetchExhibitor: async ({ commit, dispatch }: any, payload: number) => {
    dispatch('clearError');
    commit('SET_LOADING', true);

    await new Promise((resolve, reject) => {
      exhibitorService
        .fetchExhibitor(payload)
        .then(exhibitor => {
          const exhibitors = [...state.exhibitors];
          const index = exhibitors.findIndex(
            (e: Exhibitor) => e.id === payload
          );
          if (index === -1) {
            commit('ADD_EXHIBITOR', exhibitor);
          } else {
            commit('UPDATE_EXHIBITOR', exhibitor);
          }
          resolve();
        })
        .catch(err => {
          commit('SET_ERROR', err);
          //TODO: LF - 2020-03-08: Finalize how to handle error
          reject();
        })
        .finally(() => {
          commit('SET_LOADING', false);
        });
    });
  },

  updateExhibitor: async ({ commit, dispatch }: any, payload: any) => {
    dispatch('clearError');
    commit('SET_LOADING', true);

    await new Promise((resolve, reject) => {
      exhibitorService
        .updateExhibitor(payload.id, payload.data)
        .then(exhibitor => {
          commit('UPDATE_EXHIBITOR', exhibitor);
          resolve();
        })
        .catch(err => {
          commit('SET_ERROR', err);
          //TODO: LF - 2020-03-08: Finalize how to handle error
          reject();
        })
        .finally(() => {
          commit('SET_LOADING', false);
        });
    });
  },

  deleteExhibitor: async ({ commit, dispatch }: any, payload: number) => {
    dispatch('clearError');
    commit('SET_LOADING', true);

    await new Promise((resolve, reject) => {
      exhibitorService
        .deleteExhibitor(payload)
        .then(() => {
          commit('REMOVE_EXHIBITOR', payload);
          resolve();
        })
        .catch(err => {
          commit('SET_ERROR', err);
          //TODO: LF - 2020-03-08: Finalize how to handle error
          reject();
        })
        .finally(() => {
          commit('SET_LOADING', false);
        });
    });
  },

  optionsExhibitor: async ({ commit, dispatch }: any) => {
    dispatch('clearError');
    commit('SET_LOADING', true);

    await new Promise((resolve, reject) => {
      exhibitorService
        .optionsExhibitor()
        .then((res: any) => {
          commit('SET_OPTIONS', res.actions.POST);
          resolve();
        })
        .catch((err: any) => {
          commit('SET_ERROR', err);
          //TODO: LF - 2020-03-08: Finalize how to handle error
          reject();
        })
        .finally(() => {
          commit('SET_LOADING', false);
        });
    });
  },

  // Exhibitor category actions
  createCategory: async (
    { commit, dispatch }: any,
    payload: ExhibitorCategory
  ) => {
    dispatch('clearError');
    commit('SET_LOADING', true);

    await new Promise((resolve, reject) => {
      exhibitorService.categoryService
        .createCategory(payload)
        .then(category => {
          commit('ADD_CATEGORY', category);
          resolve();
        })
        .catch(err => {
          commit('SET_ERROR', err);
          //TODO: LF - 2020-03-08: Finalize how to handle error
          reject();
        })
        .finally(() => {
          commit('SET_LOADING', false);
        });
    });
  },

  fetchCategories: async (
    { commit, dispatch }: any,
    payload: { event: string; }
  ) => {
    dispatch('clearError');
    commit('SET_LOADING', true);

    return await new Promise((resolve, reject) => {
      exhibitorService.categoryService
        .fetchCategories(payload.event)
        .then(categories => {
          commit('SET_CATEGORIES', categories);
          resolve(categories);
        })
        .catch(err => {
          commit('SET_ERROR', err);
          //TODO: LF - 2020-03-08: Finalize how to handle error
          reject(err);
        })
        .finally(() => {
          commit('SET_LOADING', false);
        });
    });
  },

  updateCategory: async (
    { commit, dispatch }: any,
    payload: ExhibitorCategory
  ) => {
    dispatch('clearError');
    commit('SET_LOADING', true);

    await new Promise((resolve, reject) => {
      exhibitorService.categoryService
        .updateCategory(payload.id, payload)
        .then(category => {
          commit('UPDATE_CATEGORY', category);
          resolve();
        })
        .catch(err => {
          commit('SET_ERROR', err);
          //TODO: LF - 2020-03-08: Finalize how to handle error
          reject();
        })
        .finally(() => {
          commit('SET_LOADING', false);
        });
    });
  },

  deleteExhibitorCategory: async (
    { commit, dispatch }: any,
    payload: number
  ) => {
    dispatch('clearError');
    commit('SET_LOADING', true);

    await new Promise((resolve, reject) => {
      exhibitorService.categoryService
        .deleteCategory(payload)
        .then(() => {
          commit('REMOVE_CATEGORY', payload);
          resolve();
        })
        .catch(err => {
          commit('SET_ERROR', err);
          //TODO: LF - 2020-03-08: Finalize how to handle error
          reject();
        })
        .finally(() => {
          commit('SET_LOADING', false);
        });
    });
  },

  optionsCategory: async ({ commit, dispatch }: any, payload: number) => {
    dispatch('clearError');
    commit('SET_LOADING', true);

    await new Promise((resolve, reject) => {
      exhibitorService.categoryService
        .optionsCategory()
        .then(payload => {
          commit('SET_OPTIONS_CATEGORY', payload.actions.POST);
          resolve();
        })
        .catch(err => {
          commit('SET_ERROR', err);
          //TODO: LF - 2020-03-08: Finalize how to handle error
          reject();
        })
        .finally(() => {
          commit('SET_LOADING', false);
        });
    });
  }
};

export const getters = {
  getExhibitorById: (state: any) => (id: number) => {
    return state.exhibitors.find((exhibitor: Exhibitor) => exhibitor.id === id);
  },

  getCategoryById: (state: any) => (id: number) => {
    return state.categories.find(
      (category: ExhibitorCategory) => category.id === id
    );
  }
};
